/* eslint-disable camelcase */
import { FeedbackMap } from 'components/feedback/hooks/types';
import {
  getMessageListItemsForSubscriber,
  isHandoffEvent,
  MessageListItem,
} from 'components/responses/utils/messages';
import { SMS_SALES_VIEW_LOCAL_STORAGE_KEY } from 'constants/constants';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { isSubscriberActivityEvent, VIEW_OPTIONS } from '../constants';
import { useView } from '../hooks/useView';

export interface MessageListProps {
  subscriber: any;
  messageThreadRef: React.RefObject<HTMLUListElement>;
  feedback_items?: any;
  itFeedbackItems?: FeedbackMap;
  onEmptyStateChange?: (empty: boolean) => void;
}

/* MessageList component for responses AND SMS Sales */

const StyledUL = styled.ul<{ initialized: boolean }>`
  list-style: none;
  height: 100%;
  position: relative;

  & > li {
    margin-top: var(--spacing-2);
    position: relative;
    z-index: 2;

    &:empty:not(:last-child) {
      display: none;
    }

    &.message-type-timestamp + .message-type-timestamp {
      margin-top: var(--spacing-1);
    }

    &.message-type-system:not(:empty) + .message-type-system:not(:empty) {
      margin-top: 0;
    }

    &.message-type-incoming {
      & + .message-type-incoming {
        margin-top: var(--spacing-1);
      }

      &:has(+ .message-type-incoming) {
        .message-bubble {
          border-bottom-left-radius: var(--border-radius-medium);
        }
        .message-avatar {
          display: none;
        }
      }
    }

    &.message-type-outgoing {
      & + .message-type-outgoing {
        margin-top: var(--spacing-1);
      }

      &:has(+ .message-type-outgoing) {
        .message-bubble {
          border-bottom-right-radius: var(--border-radius-medium);
        }
        .message-avatar {
          display: none;
        }
      }
    }

    &.message-type-bub {
      & + .message-type-bub {
        margin-top: var(--spacing-1);
      }

      &:has(+ .message-type-bub) {
        .message-bubble {
          border-bottom-right-radius: var(--border-radius-medium);
        }
        .message-avatar {
          display: none;
        }
      }
    }
  }

  & > li.highlight-message {
    @keyframes highlightFade {
      0% {
        background-color: transparent;
      }
      15% {
        background-color: color-mix(
          in srgb,
          var(--highlight-color) 20%,
          transparent
        );
      }
      100% {
        background-color: transparent;
      }
    }
    animation: highlightFade 2s var(--transition-easing) forwards;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

export const MessageList = ({
  subscriber,
  messageThreadRef,
  feedback_items,
  itFeedbackItems,
  onEmptyStateChange,
}: MessageListProps): JSX.Element => {
  const { view } = useView();
  const messageItems: MessageListItem[] = useMemo(
    () =>
      getMessageListItemsForSubscriber(
        subscriber,
        view,
        feedback_items,
        itFeedbackItems,
      ),
    [subscriber, view, feedback_items, itFeedbackItems],
  );

  const getMessageType = (props: any) => {
    if (!props) return;
    let type = 'system';
    if (props.group?.type === 'message-group') {
      type = 'timestamp';
    }
    if (props.message) {
      if (props.message.direction) {
        type = props.message.direction;
      }
      if (
        props.message.direction === 'outgoing' &&
        (!props.message.user_first_name || props.message.user_first_name === '')
      ) {
        type = 'bub';
      }
    }

    // FIX: Handle GroupedProfileEvent components
    if (props.messages) {
      type = 'profile'; // Treat grouped profile events as profile type, not system
    }

    return type;
  };

  const visibleMessages = useMemo(() => {
    const shouldShowMessage = (item: MessageListItem) => {
      const messageType = getMessageType(item.el?.props);
      const isSystemMessage = messageType === 'system';
      const isTimestampMessage = messageType === 'timestamp';
      const message = item.el?.props?.message ?? {};
      const isSenderTypeMessage = message.type === 'sender';
      const isHandoff = isHandoffEvent(message);
      // Subscriber-activity events (checkout, order, search, opt-out) render as
      // system messages but describe what the subscriber did, and are what the
      // Conversations "Events" filter offers — so Merchant view keeps them.
      const isActivityEvent = isSubscriberActivityEvent(message);
      const hideSystemMessages =
        localStorage.getItem(SMS_SALES_VIEW_LOCAL_STORAGE_KEY) ===
        VIEW_OPTIONS.MERCHANT;

      const shouldShowView =
        !((isSystemMessage || isTimestampMessage) && hideSystemMessages) ||
        isSenderTypeMessage ||
        isHandoff ||
        isActivityEvent;

      return shouldShowView;
    };

    return messageItems.filter(shouldShowMessage);
  }, [messageItems]);

  useEffect(() => {
    onEmptyStateChange?.(visibleMessages.length === 0);
  }, [onEmptyStateChange, visibleMessages.length]);

  return (
    <StyledUL
      initialized={Boolean(subscriber)}
      ref={messageThreadRef}
      data-testid="message-list"
    >
      {visibleMessages.length === 0 ? (
        <li className="message-type-empty" key="empty-state">
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--spacing-4)',
              paddingTop: 'var(--spacing-10)',
              color: 'var(--gray-7)',
            }}
          >
            No message or event data available
          </div>
        </li>
      ) : (
        visibleMessages.map((item) => (
          <li
            className={`message-type-${getMessageType(item.el?.props)}`}
            id={`message-${item.key}`}
            key={item.key}
          >
            {item.el}
          </li>
        ))
      )}
    </StyledUL>
  );
};
